import React, { useMemo, useState } from "react";
import {
  Table,
  Button,
  SpaceBetween,
  Box,
  ExpandableSection,
  Link,
  Input,
} from "@amzn/awsui-components-react";
import { SavedFolder } from "../../types/folder";
import { SavedSearchItem } from "../../types/common";
import HealthStatusIndicator from "../common/HealthStatusIndicator";
import { HealthStatus } from "../../types/health";
import { getGlMapping } from "../../hooks/useSummaryData";

interface FolderTableProps {
  folders: SavedFolder[];
  onEdit: (folder: SavedFolder) => void;
  onDelete: (folderId: string) => void;
  onShare: (folder: SavedFolder) => void;
  onDeleteTransaction: (folderId: string, transactionId: string) => void;
  onUpdateTransactionLabels: (
    folderId: string,
    transactionId: string,
    labels: string[]
  ) => void;
}

export function FolderTable({
  folders,
  onEdit,
  onDelete,
  onShare,
  onDeleteTransaction,
  onUpdateTransactionLabels,
}: FolderTableProps) {
  const transactionColumnDefinitions = useMemo(
    () => [
      {
        id: "id",
        header: "Transaction ID",
        cell: (item: SavedSearchItem) => (
          <Link
            href={`?stage=${encodeURIComponent(
              item.stage
            )}&transactionId=${encodeURIComponent(item.id)}`}
          >
            {item.id}
          </Link>
        ),
      },
      {
        id: "labels",
        header: "Labels",
        cell: (item: SavedSearchItem) => {
          const [isEditing, setIsEditing] = useState(false);
          const [labelInput, setLabelInput] = useState("");
          const [editingLabelIndex, setEditingLabelIndex] = useState<
            number | null
          >(null);

          const folder = folders.find((f) =>
            f.transactions?.some((t) => t.id === item.id)
          );

          const handleSave = () => {
            if (labelInput.trim() && folder) {
              const newLabels = [...(item.labels || [])];

              if (editingLabelIndex !== null) {
                newLabels[editingLabelIndex] = labelInput.trim();
              } else {
                newLabels.push(labelInput.trim());
              }
              onUpdateTransactionLabels(folder.id, item.id, newLabels);
            }
            setLabelInput("");
            setIsEditing(false);
            setEditingLabelIndex(null);
          };

          const handleKeyDown = ({ detail }: { detail: { key: string } }) => {
            if (detail.key === "Enter") {
              handleSave();
            } else if (detail.key === "Escape") {
              setIsEditing(false);
              setLabelInput("");
              setEditingLabelIndex(null);
            }
          };

          const removeLabel = (labelToRemove: string) => {
            if (folder) {
              onUpdateTransactionLabels(
                folder.id,
                item.id,
                (item.labels || []).filter((label) => label !== labelToRemove)
              );
            }
          };

          const startEditing = (label: string, index: number) => {
            setLabelInput(label);
            setEditingLabelIndex(index);
            setIsEditing(true);
          };

          return (
            <SpaceBetween direction="horizontal" size="xs">
              {item.labels?.map((label, index) =>
                editingLabelIndex === index && isEditing ? (
                  <Input
                    key={`${label}-input`}
                    value={labelInput}
                    onChange={({ detail }) => setLabelInput(detail.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Edit label..."
                    autoFocus
                    onBlur={() => {
                      if (labelInput.trim()) {
                        handleSave();
                      } else {
                        setIsEditing(false);
                        setEditingLabelIndex(null);
                      }
                    }}
                  />
                ) : (
                  <span key={label} className="awsui-label">
                    {label}
                    <Button
                      iconName="edit"
                      variant="icon"
                      onClick={() => startEditing(label, index)}
                      ariaLabel={`Edit ${label} label`}
                    />
                    <Button
                      iconName="remove"
                      variant="icon"
                      onClick={() => removeLabel(label)}
                      ariaLabel={`Remove ${label} label`}
                    />
                  </span>
                )
              )}
              {isEditing && editingLabelIndex === null ? (
                <Input
                  value={labelInput}
                  onChange={({ detail }) => setLabelInput(detail.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter label name"
                  autoFocus
                  onBlur={() => {
                    if (labelInput.trim()) {
                      handleSave();
                    } else {
                      setIsEditing(false);
                    }
                  }}
                />
              ) : (
                !isEditing && (
                  <Button
                    iconName="add-plus"
                    variant="icon"
                    onClick={() => {
                      setIsEditing(true);
                      setEditingLabelIndex(null);
                    }}
                    ariaLabel="Add label"
                  />
                )
              )}
            </SpaceBetween>
          );
        },
      },

      {
        id: "stage",
        header: "Stage",
        cell: (item: SavedSearchItem) => item.stage,
      },
      {
        id: "gl",
        header: "GL",
        cell: (item: SavedSearchItem) => getGlMapping(item.gl),
      },
      {
        id: "country",
        header: "Country",
        cell: (item: SavedSearchItem) => item.country,
      },
      {
        id: "healthStatus",
        header: "Status",
        cell: (item: SavedSearchItem) => (
          <HealthStatusIndicator
            healthStatus={(item.status || "unknown") as HealthStatus}
          />
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (item: SavedSearchItem) => (
          <Button
            onClick={() => {
              const folder = folders.find((f) =>
                f.transactions?.some((t) => t.id === item.id)
              );
              if (folder) {
                onDeleteTransaction(folder.id, item.id);
              }
            }}
            iconName="remove"
            variant="icon"
            ariaLabel="Delete transaction"
          />
        ),
      },
    ],
    [folders, onDeleteTransaction, onUpdateTransactionLabels]
  );

  const columnDefinitions = useMemo(
    () => [
      {
        id: "name",
        header: "Folder Name",
        cell: (item: SavedFolder) => (
          <ExpandableSection
            headerText={item.name}
            headerDescription={`${item.transactions?.length ?? 0} transactions`}
            variant="container"
          >
            <SpaceBetween direction="vertical" size="s">
              <Box>
                {item.transactions?.length ? (
                  <Table
                    columnDefinitions={transactionColumnDefinitions}
                    items={item.transactions}
                    variant="embedded"
                    empty={
                      <Box textAlign="center" color="inherit">
                        <b>No transactions in this folder</b>
                      </Box>
                    }
                  />
                ) : (
                  "No transactions saved in this folder"
                )}
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        ),
        sortingField: "name",
      },
      {
        id: "actions",
        header: "Actions",
        width: 100,
        cell: (item: SavedFolder) => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button
              onClick={() => onEdit(item)}
              iconName="edit"
              variant="icon"
              ariaLabel="Edit folder"
            />
            {process.env.NODE_ENV === "development" && (
              <Button
                onClick={() => onShare(item)}
                iconName="share"
                variant="icon"
                ariaLabel="Share folder"
              />
            )}
            <Button
              onClick={() => onDelete(item.id)}
              iconName="remove"
              variant="icon"
              ariaLabel="Delete folder"
            />
          </div>
        ),
      },
    ],
    [onEdit, onDelete, onShare, transactionColumnDefinitions]
  );

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={folders}
      loadingText="Loading folders"
      empty={
        <Box textAlign="center" color="inherit">
          <b>No folders</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            Create a new folder to get started.
          </Box>
        </Box>
      }
    />
  );
}
